/** SUNTRONIX ONE — Scan Asset.
 *
 *  Uses the browser's native BarcodeDetector when available (Chrome/Edge on
 *  Android, Safari 17+ on iOS, Chrome/Edge on desktop with camera).  Falls
 *  back to a manual "type the serial" input on browsers without support
 *  so operators can never end up on a dead-end page.
 *
 *  On any successful scan / lookup, jumps straight to the Golden Asset
 *  Record — never a search-results page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Camera, Loader2, ScanLine, KeyboardIcon, ArrowLeft, AlertTriangle } from "lucide-react";
import PageHeader from "@/components/app/PageHeader";

const SUPPORTED_FORMATS = [
  "code_128", "code_39", "codabar", "code_93",
  "ean_13", "ean_8", "itf", "upc_a", "upc_e",
  "qr_code", "data_matrix", "pdf417", "aztec",
];

export default function ScanAsset() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const openAsset = useCallback(async (code) => {
    if (!code || busy) return;
    setBusy(true);
    setLastCode(code);
    try {
      const { data } = await api.get("/asset-search/by-serial",
        { params: { q: code, limit: 3 } });
      if (data.kind === "exact") {
        stopCamera();
        navigate(`/inventory/passport/${data.result.id}`);
      } else if (data.kind === "duplicates") {
        toast.error(
          `Duplicate serial detected — ${data.count} records share ${code}.`);
      } else if (data.kind === "partial" && data.results?.length === 1) {
        stopCamera();
        navigate(`/inventory/passport/${data.results[0].id}`);
      } else {
        toast.error(`No device matches ${code} in your workspace.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  }, [busy, navigate, stopCamera]);



  const startCamera = useCallback(async () => {
    setError(null);
    // eslint-disable-next-line no-undef
    if (typeof BarcodeDetector === "undefined") {
      setSupported(false);
      setError("This browser doesn't support live barcode scanning. Use manual entry below.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      // eslint-disable-next-line no-undef
      const detector = new BarcodeDetector({ formats: SUPPORTED_FORMATS });
      const scan = async () => {
        if (!videoRef.current || !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length) {
            const value = codes[0].rawValue?.trim();
            if (value && value !== lastCode) {
              await openAsset(value);
            }
          }
        } catch { /* transient — keep scanning */ }
        rafRef.current = requestAnimationFrame(scan);
      };
      rafRef.current = requestAnimationFrame(scan);
    } catch (e) {
      setError(e.message || "Camera access denied. Use manual entry below.");
      setScanning(false);
    }
  }, [lastCode, openAsset]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) openAsset(manual.trim());
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="scan-asset-page">
      <PageHeader
        eyebrow={<button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-3 w-3" /> Back
        </button>}
        title="Scan asset"
        context="Point your camera at the barcode, QR code or asset tag. We look it up in your workspace and jump straight to the Golden Asset Record."
        testId="scan-asset-header"
      />

      <div className="st-panel p-4 space-y-3">
        <div className="relative rounded-md overflow-hidden bg-slate-950 aspect-[4/3]">
          <video ref={videoRef} playsInline muted
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="scan-asset-video" />
          {!scanning && (
            <div className="absolute inset-0 grid place-items-center text-slate-400 text-[13px]">
              <div className="flex flex-col items-center gap-2">
                <ScanLine className="h-8 w-8" />
                <div>Tap "Start camera" to begin scanning</div>
              </div>
            </div>
          )}
          {scanning && (
            <div className="absolute inset-0 pointer-events-none border-2 border-emerald-400/60 rounded-md m-6 animate-pulse" />
          )}
          {busy && (
            <div className="absolute inset-0 grid place-items-center bg-slate-950/60 text-white text-[13px]"
              data-testid="scan-asset-busy">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Looking up {lastCode}…
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!scanning ? (
            <Button onClick={startCamera} data-testid="scan-asset-start">
              <Camera className="h-3.5 w-3.5 mr-1.5" /> Start camera
            </Button>
          ) : (
            <Button variant="outline" onClick={stopCamera}
              data-testid="scan-asset-stop">
              Stop camera
            </Button>
          )}
          {!supported && (
            <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Live scan not supported here
            </span>
          )}
        </div>

        {error && (
          <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
            {error}
          </div>
        )}
      </div>

      <div className="st-panel p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <KeyboardIcon className="h-3 w-3" /> Or type / paste
        </div>
        <form onSubmit={submitManual} className="flex gap-2">
          <Input value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Serial, tag or IMEI"
            data-testid="scan-asset-manual" />
          <Button type="submit" disabled={!manual.trim() || busy}
            data-testid="scan-asset-manual-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Look up"}
          </Button>
        </form>
      </div>
    </div>
  );
}
